<template>
  <ul class="tabs-header">
    <li
      v-for="meta in tabsMeta"
      :key="meta.id"
      :class="{ active: activeTab === meta.id, disabled: meta.disabled }"
      @click="!meta.disabled && selectTab(meta.id)"
    >
      {{ meta.title }}
    </li>
  </ul>
  <div
    v-for="tabVNode in tabs"
    :key="tabVNode.props!.id"
    v-show="activeTab === tabVNode.props!.id"
    :class="tabClass"
    class="tab-grow"
  >
    <component :is="tabVNode" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useSlots, VNode, watch } from 'vue';
import { TabProps } from './tab/CustomTab.vue';

const props = withDefaults(
  defineProps<{
    modelValue: string | number;
    tabClass?: string;
    disabled?: boolean;
  }>(),
  {
    modelValue: '',
    disabled: false
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number): void;
}>();

const activeTab = ref<string | number>('');

const slots = useSlots();

const tabs = computed<VNode[]>(() => {
  return slots.default ? slots.default() : [];
});

const tabsMeta = computed<TabProps[]>(() => {
  return tabs.value.map((tabVNode) => {
    return tabVNode.props as TabProps;
  });
});

onMounted(() => {
  if (tabsMeta.value.length) {
    const initial = props.modelValue;
    const exists = tabsMeta.value.some((meta) => meta.id === initial);
    if (exists) {
      activeTab.value = initial;
    } else {
      activeTab.value = tabsMeta.value[0].id;
      emit('update:modelValue', activeTab.value);
    }
  }
});

watch(
  () => props.modelValue,
  (newValue) => {
    const exists = tabsMeta.value.some((meta) => meta.id === newValue);
    if (exists) {
      activeTab.value = newValue;
    }
  }
);

function selectTab(id: string | number): void {
  activeTab.value = id;
  emit('update:modelValue', id);
}
</script>

<style scoped lang="scss">
.tabs-header {
  display: flex;
  list-style: none;
  background: $color-neutral-0;
  gap: $space-1;
  padding: $space-1;
  border-radius: $radius-1;
  margin: 0 0 $space-1 0;

  li {
    padding: $space-1;
    cursor: pointer;
    user-select: none;
    transition: background 0.3s ease;
    flex-grow: 1;
    text-align: center;

    &.active {
      background: $color-primary-500;
      font-weight: bold;
      border-radius: $radius-2;
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  .disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.tab-grow {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
}
</style>
